import { v4 as uuidv4 } from 'uuid';

import {
  listMessages,
  bulkCreateMessages,
} from '@wix/ambassador-innovation-widget-v1-message/http';
import { getConversation } from '@wix/ambassador-innovation-widget-v1-conversation/http';
import { submitContact } from '@wix/ambassador-contacts-v4-submit-contact/http';
import {
  ListMessagesRequest,
  Sender,
  MessageType,
} from '@wix/ambassador-innovation-widget-v1-message/types';
import {
  EmailTag,
  PhoneTag,
  SubmitContactRequest,
} from '@wix/ambassador-contacts-v4-submit-contact/types';

import { senderIsVisitor } from '@/utils/sender';

import { FormData } from 'AiAssistantWidget/Widget/components';
import { FormSubmissionPayload } from 'AiAssistantWidget/Widget/components/ChatMessagePayload/types';

import { createAppAsyncThunk } from '../createAppAsyncThunk';
import { selectLayout } from '../layout';
import { flagsSlice } from '../flags';

import {
  formatMessage,
  formatMessages,
  isAllowedMessage,
  messagesQueryBuilder,
  SPECIAL_MESSAGE_TYPES,
} from './utils';
import {
  getDummyAssistantMessages,
  getDummyBubbleMessages,
  getDummyCardMessages,
  getDummyContactForm,
  getDummyMessages,
  getDummyVisitorMessages,
} from './dummyMessages';
import { messagesSlice } from './messagesSlice';
import {
  AssistantAction,
  hasAssistantActions,
  Message,
  WidgetApi,
} from './model';
import {
  ConversationChannel,
  createConversationChannel,
  DuplexerMergeConversationsMessage,
  DuplexerMessage,
  isMessageTypingEvent,
  isNewMessageEvent,
} from './conversationChannel';

import { selectMessages } from '.';

export const enum DummyMessageType {
  Visitor,
  Assistant,
  Card,
  Bubble,
  ContactForm,
}

export const getEditorMockMessages = createAppAsyncThunk<
  void,
  DummyMessageType | void
>('messages/getMock', async (dummyMessageType, { dispatch, extra: { t } }) => {
  let messages: WidgetApi.Message[];
  switch (dummyMessageType) {
    case DummyMessageType.Assistant:
      messages = getDummyAssistantMessages(t);
      break;
    case DummyMessageType.Visitor:
      messages = getDummyVisitorMessages(t);
      break;
    case DummyMessageType.Card:
      messages = getDummyCardMessages(t);
      break;
    case DummyMessageType.Bubble:
      messages = getDummyBubbleMessages(t);
      break;
    case DummyMessageType.ContactForm:
      messages = getDummyContactForm(t);
      break;
    default:
      messages = getDummyMessages(t);
      break;
  }

  dispatch(
    messagesSlice.actions.resetMessages({
      messages: formatMessages(messages),
      pagingMetadata: { tooManyToCount: true, cursors: {} },
    }),
  );
});

export const getMessagesHistory = createAppAsyncThunk(
  'messages/get',
  async (
    query: ListMessagesRequest | undefined = messagesQueryBuilder(),
    { dispatch, extra: { httpClient } },
  ) => {
    const resp = await httpClient.request(listMessages(query));
    const { messages, pagingMetadata } = resp.data;

    dispatch(
      messagesSlice.actions.addMessages({
        pagingMetadata,
        messages: formatMessages(messages?.filter(isAllowedMessage)),
      }),
    );
  },
);

export const getConversationId = createAppAsyncThunk(
  'messages/getConversationId',
  async (_, { dispatch, extra: { httpClient, panorama } }) => {
    const panoramaTransaction = panorama.transaction('get-conversation-id');
    panoramaTransaction.start();
    const {
      data: { conversation },
    } = await httpClient.request(getConversation({}));
    panoramaTransaction.finish();

    if (!conversation?.id) {
      throw new Error('No conversationId in response!');
    }

    dispatch(messagesSlice.actions.setConversationId(conversation!.id!));
    dispatch(flagsSlice.actions.setIsContact(Boolean(conversation!.contact)));

    return conversation!.id;
  },
);

export const subscribeForNewMessages = createAppAsyncThunk(
  'messages/subscribe',
  async (_, { dispatch, getState, extra: { duplexerConnection } }) => {
    if (!duplexerConnection) {
      // No need to listen in Editor
      return;
    }

    /**
     * TODO: set subscription/connection status in store,
     * handle disconnect/errors with relative status, resubscribe
     */

    const conversation =
      selectMessages.conversationId(getState()) ??
      (await dispatch(getConversationId()).unwrap());

    const conversationChannel = createConversationChannel(
      conversation,
      duplexerConnection,
    );

    conversationChannel.onNewMessageEvent((event) =>
      dispatch(newMessageEvent(event)),
    );
    conversationChannel.onNewConversationEvent((event) =>
      dispatch(
        newConversationEvent({
          conversationChannel,
          event,
        }),
      ),
    );

    return () => {
      conversationChannel.close();
    };
  },
);

const newMessageEvent = createAppAsyncThunk(
  'messages/newMessageEvent',
  async (event: DuplexerMessage, { dispatch, getState }) => {
    if (isNewMessageEvent(event)) {
      dispatch(messagesSlice.actions.setIsTyping(false));

      if (!isAllowedMessage(event)) {
        return;
      }

      const pendingMessages = selectMessages.pendingMessages(getState());
      if (
        pendingMessages &&
        (senderIsVisitor(event.sender) ||
          (event.messageType && SPECIAL_MESSAGE_TYPES.has(event.messageType)))
      ) {
        // It is possible to get event with a message id before we got API response
        // in which case 2 messages are created. This mechanic should solve such cases
        return;
      }

      const message = formatMessage(event);
      dispatch(messagesSlice.actions.addOrUpdateMessage(message));

      const state = getState();
      if (selectLayout.isMinimized(state)) {
        dispatch(messagesSlice.actions.setUnread(true));
      }

      if (hasAssistantActions(message)) {
        await dispatch(assistantActions(message.assistantActions));
      }
    } else if (isMessageTypingEvent(event)) {
      // Typing notification
      dispatch(messagesSlice.actions.setIsTyping(true));
      // TODO: add timed removal of typing indicator
    }
  },
);

const assistantActions = createAppAsyncThunk(
  'messages/assistantActions',
  async (actions: string[], { extra: { siteEcom } }) => {
    return Promise.all(
      actions.map((action) => {
        switch (action) {
          case AssistantAction.AddToCurrentCart:
          case AssistantAction.RemoveLineItemsFromCurrentCart:
          case AssistantAction.UpdateCurrentCart:
          case AssistantAction.UpdateCurrentCartLineItemQuantity:
            return siteEcom.refreshCart();
          default:
            return Promise.resolve();
        }
      }),
    );
  },
);

const newConversationEvent = createAppAsyncThunk(
  'messages/newConversation',
  async (
    {
      conversationChannel,
      event,
    }: {
      conversationChannel: ConversationChannel;
      event: DuplexerMergeConversationsMessage;
    },
    { dispatch },
  ) => {
    if (event.targetConversationId) {
      conversationChannel.removeAllListeners();
      dispatch(
        messagesSlice.actions.resetConversation(event.targetConversationId),
      );
    }
  },
);
export type AddMessagePayload = Partial<Message> & {
  body: { mainText: string };
};

export const enum ActionMessageType {
  Navigation = 'action_navigation',
  AddToCart = 'action_add_to_cart',
  BookService = 'action_book_service',
}

export type AddActionMessagePayload = {
  mainText: string;
  messageType: MessageType;
  data: {
    type: ActionMessageType;
    action: string;
  };
};

export const addActionMessage = createAppAsyncThunk(
  'messages/addAction',
  async (
    { messageType, mainText, data: { type, action } }: AddActionMessagePayload,
    { dispatch, extra: { httpClient }, getState },
  ) => {
    const conversationId = selectMessages.conversationId(getState());
    const req = bulkCreateMessages({
      messages: [
        {
          sender: Sender.SITE_VISITOR,
          messageType,
          conversationId,
          body: {
            mainText,
            additionalData: [
              {
                id: uuidv4(),
                type,
                action,
              },
            ],
          },
        },
      ],
    });

    await httpClient.request(req);
  },
);

export const addMessage = createAppAsyncThunk(
  'messages/add',
  async (
    payload: AddMessagePayload | AddMessagePayload[],
    { dispatch, extra: { httpClient }, getState },
  ) => {
    const conversationId = selectMessages.conversationId(getState());

    const msgs = Array.isArray(payload) ? payload : [payload];
    const optimisticUpdateMessages: Message[] = msgs.map((msg, index) => ({
      id: uuidv4(),
      sender: Sender.SITE_VISITOR,
      messageType: MessageType.QUESTION,
      conversationId,
      createdDate: Date.now() + index,
      ...msg,
    }));

    optimisticUpdateMessages.forEach((msg) =>
      dispatch(messagesSlice.actions.addOptimisticMessage(msg)),
    );

    const req = bulkCreateMessages({
      messages: optimisticUpdateMessages.map(
        ({ body, sender, messageType, answerTo }) => ({
          sender,
          messageType,
          answerTo,
          body,
        }),
      ),
    });

    const {
      data: { messages },
      status,
    } = await httpClient.request(req);
    if (status !== 200) {
      // TODO: handle error with UI?
      throw new Error('Failed to add message');
    }

    if (messages?.some((m) => !m.id)) {
      // TODO: should not happen, if yes, fix or make UI
      throw new Error('Some messages were not created');
    }

    messages?.forEach((m, i) => {
      const optimistic = optimisticUpdateMessages[i];
      dispatch(
        messagesSlice.actions.updateMessage({
          id: optimistic.id,
          changes: formatMessage(
            m,
            m.messageType && SPECIAL_MESSAGE_TYPES.has(m.messageType)
              ? optimistic.createdDate
              : undefined,
          ),
        }),
      );
    });
  },
);

export const setRead = createAppAsyncThunk(
  'messages/read',
  async (_, { dispatch }) => {
    dispatch(messagesSlice.actions.setUnread(false));
  },
);

export const submitVisitorAsContact = createAppAsyncThunk(
  'messages/submitContact',
  async (
    { name, email, phone, message, formId }: FormData,
    { dispatch, extra: { httpClient } },
  ) => {
    const structure: SubmitContactRequest = {
      info: {
        emails: { items: [{ email, tag: EmailTag.UNTAGGED }] },
      },
    };

    if (name) {
      structure.info!.name = { first: name };
    }
    if (phone) {
      structure.info!.phones = { items: [{ phone, tag: PhoneTag.UNTAGGED }] };
    }

    await httpClient.request(submitContact(structure));

    dispatch(
      addMessage({
        sender: Sender.SYSTEM,
        messageType: MessageType.CONTACT_FORM_SUBMITTED,
        answerTo: formId,
        body: {
          mainText: ' ',
          additionalData: [
            {
              type: 'form_submitted',
              id: uuidv4(),
              name,
              email,
              phone,
            } as FormSubmissionPayload,
          ],
        },
      }),
    );

    if (message) {
      dispatch(
        addMessage({
          body: { mainText: message },
          sender: Sender.SITE_VISITOR,
        }),
      );
    }
  },
);

export const SELECT_OPTION_ACTION_TYPE = 'select_option';

export const selectOption = createAppAsyncThunk(
  'engineActions/selectOption',
  async (
    { option, answerTo }: { option: string; answerTo: string },
    { dispatch },
  ) => {
    await dispatch(
      addMessage([
        {
          messageType: MessageType.VISITOR_OPTION_SELECTED,
          answerTo,
          body: {
            mainText: option,
            additionalData: [
              {
                id: uuidv4(),
                type: SELECT_OPTION_ACTION_TYPE,
                action: option,
              },
            ],
          },
        },
      ]),
    );
  },
);
